import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { uploadData } from 'aws-amplify/storage';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { createRecipe, createIngredient, createStep } from '../graphql/mutations';
import { listFoodMasters } from '../graphql/queries';

const client = generateClient();

const UNITS = ['g', 'ml', '大さじ', '小さじ', '少々', '適量', '枚', '個', '本', '袋'];
const AMOUNT_PRESETS = ['1', '2', '3', '50', '100', '200'];

export default function RecipeCreate() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [ingredients, setIngredients] = useState([{ name: '', amount: '', unit: 'g', suggestions: [], showSuggestions: false }]);
  const [steps, setSteps] = useState([{ description: '' }]);
  const [foodMasters, setFoodMasters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    // デフォルト公開設定をUserCharacterから取得（今回はisPublic=trueをデフォルト）
    async function loadFoodMasters() {
      try {
        const result = await client.graphql({ query: listFoodMasters, variables: { limit: 200 } });
        setFoodMasters(result.data.listFoodMasters.items);
      } catch (err) {
        console.error('食材マスタ取得エラー:', err);
      }
    }
    loadFoodMasters();
  }, []);

  // 食材名入力時にサジェスト
  const handleIngredientNameChange = (index, value) => {
    const suggestions = value.length >= 1
      ? foodMasters.filter(f =>
          f.name.includes(value) || f.yomi.startsWith(value)
        ).slice(0, 8)
      : [];
    setIngredients(prev => prev.map((ing, i) =>
      i === index ? { ...ing, name: value, suggestions, showSuggestions: suggestions.length > 0 } : ing
    ));
  };

  const selectSuggestion = (index, name) => {
    setIngredients(prev => prev.map((ing, i) =>
      i === index ? { ...ing, name, suggestions: [], showSuggestions: false } : ing
    ));
  };

  const handleIngredientChange = (index, field, value) => {
    setIngredients(prev => prev.map((ing, i) =>
      i === index ? { ...ing, [field]: value } : ing
    ));
  };

  const addIngredient = () => setIngredients(prev => [...prev, { name: '', amount: '', unit: 'g', suggestions: [], showSuggestions: false }]);
  const removeIngredient = (index) => setIngredients(prev => prev.filter((_, i) => i !== index));

  const addStep = () => setSteps(prev => [...prev, { description: '' }]);
  const removeStep = (index) => setSteps(prev => prev.filter((_, i) => i !== index));
  const handleStepChange = (index, value) => setSteps(prev => prev.map((s, i) => i === index ? { description: value } : s));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setSaveMsg('画像は5MB以下にしてください');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!title.trim()) { setSaveMsg('タイトルを入力してください'); return; }
    setSaving(true);
    setSaveMsg('');
    try {
      let imageUrl = null;
      if (imageFile) {
        const key = `recipes/${Date.now()}_${imageFile.name}`;
        await uploadData({ key, data: imageFile, options: { contentType: imageFile.type } }).result;
        imageUrl = key;
      }

      const recipeResult = await client.graphql({
        query: createRecipe,
        variables: { input: { title, description, isPublic, imageUrl } }
      });
      const recipeId = recipeResult.data.createRecipe.id;

      // 材料を保存
      for (const ing of ingredients) {
        if (!ing.name.trim()) continue;
        await client.graphql({
          query: createIngredient,
          variables: { input: { recipeId, name: ing.name, amount: ing.amount, unit: ing.unit } }
        });
      }

      // 手順を保存
      for (let i = 0; i < steps.length; i++) {
        if (!steps[i].description.trim()) continue;
        await client.graphql({
          query: createStep,
          variables: { input: { recipeId, order: i + 1, description: steps[i].description } }
        });
      }

      setSaveMsg('レシピを保存しました！');
      setTimeout(() => navigate('/timeline'), 1200);
    } catch (err) {
      console.error('保存エラー:', err);
      setSaveMsg('保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.container}>
        <h1 style={S.heading}>🍳 レシピを作成</h1>

        {/* 画像アップロード */}
        <div style={S.imageArea} onClick={() => fileInputRef.current.click()}>
          {imagePreview
            ? <img src={imagePreview} alt="preview" style={S.imagePreview} />
            : <div style={S.imagePlaceholder}><span style={{ fontSize: '40px' }}>📷</span><p style={{ margin: '8px 0 0', color: '#95a5a6', fontSize: '13px' }}>タップして写真を追加</p></div>
          }
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
        </div>

        {/* タイトル */}
        <div style={S.section}>
          <label style={S.label}>レシピタイトル <span style={S.required}>*</span></label>
          <input
            style={S.input}
            placeholder="例：鶏むね肉のさっぱり塩レモン炒め"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={50}
          />
        </div>

        {/* 説明 */}
        <div style={S.section}>
          <label style={S.label}>一言説明 <span style={S.charCount}>{description.length}/200</span></label>
          <textarea
            style={{ ...S.input, height: '80px', resize: 'none' }}
            placeholder="レシピの紹介文（タイムラインに表示されます）"
            value={description}
            onChange={e => setDescription(e.target.value.slice(0, 200))}
          />
        </div>

        {/* 公開設定 */}
        <div style={S.section}>
          <label style={S.label}>公開設定</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            {[{ label: '🌐 公開', value: true }, { label: '🔒 非公開', value: false }].map(opt => (
              <button
                key={String(opt.value)}
                onClick={() => setIsPublic(opt.value)}
                style={{
                  ...S.toggleBtn,
                  background: isPublic === opt.value ? '#e74c3c' : '#ecf0f1',
                  color: isPublic === opt.value ? '#fff' : '#555',
                }}
              >{opt.label}</button>
            ))}
          </div>
        </div>

        {/* 材料 */}
        <div style={S.section}>
          <label style={S.label}>材料</label>
          {ingredients.map((ing, i) => (
            <div key={i} style={{ position: 'relative', marginBottom: '10px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* 食材名入力 */}
                <div style={{ flex: 2, position: 'relative' }}>
                  <input
                    style={{ ...S.input, margin: 0 }}
                    placeholder="食材名"
                    value={ing.name}
                    onChange={e => handleIngredientNameChange(i, e.target.value)}
                    onBlur={() => setTimeout(() => setIngredients(prev => prev.map((x, j) => j === i ? { ...x, showSuggestions: false } : x)), 150)}
                  />
                  {/* サジェストドロップダウン */}
                  {ing.showSuggestions && (
                    <div style={S.suggestionBox}>
                      {ing.suggestions.map(s => (
                        <div key={s.id} style={S.suggestionItem} onMouseDown={() => selectSuggestion(i, s.name)}>
                          {s.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 量プリセット＋入力 */}
                <div style={{ flex: 1 }}>
                  <input
                    style={{ ...S.input, margin: 0 }}
                    placeholder="量"
                    value={ing.amount}
                    onChange={e => handleIngredientChange(i, 'amount', e.target.value)}
                  />
                </div>

                {/* 単位選択 */}
                <select
                  style={{ ...S.input, margin: 0, flex: 1 }}
                  value={ing.unit}
                  onChange={e => handleIngredientChange(i, 'unit', e.target.value)}
                >
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>

                {/* 削除 */}
                {ingredients.length > 1 && (
                  <button onClick={() => removeIngredient(i)} style={S.deleteBtn}>✕</button>
                )}
              </div>

              {/* 量プリセットボタン */}
              <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                {AMOUNT_PRESETS.map(p => (
                  <button key={p} onClick={() => handleIngredientChange(i, 'amount', p)} style={S.presetBtn}>{p}</button>
                ))}
                {['少々', '適量'].map(p => (
                  <button key={p} onClick={() => { handleIngredientChange(i, 'amount', p); handleIngredientChange(i, 'unit', ''); }} style={S.presetBtn}>{p}</button>
                ))}
              </div>
            </div>
          ))}
          <button onClick={addIngredient} style={S.addBtn}>＋ 材料を追加</button>
        </div>

        {/* 手順 */}
        <div style={S.section}>
          <label style={S.label}>作り方</label>
          {steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'flex-start' }}>
              <div style={S.stepNumber}>{i + 1}</div>
              <textarea
                style={{ ...S.input, margin: 0, flex: 1, height: '70px', resize: 'none' }}
                placeholder={`手順${i + 1}を入力...`}
                value={step.description}
                onChange={e => handleStepChange(i, e.target.value)}
              />
              {steps.length > 1 && (
                <button onClick={() => removeStep(i)} style={S.deleteBtn}>✕</button>
              )}
            </div>
          ))}
          <button onClick={addStep} style={S.addBtn}>＋ 手順を追加</button>
        </div>

        {/* 保存ボタン */}
        {saveMsg && <p style={{ textAlign: 'center', color: saveMsg.includes('失敗') ? '#e74c3c' : '#27ae60', fontWeight: 'bold', marginBottom: '10px' }}>{saveMsg}</p>}
        <button onClick={handleSubmit} disabled={saving} style={{ ...S.submitBtn, opacity: saving ? 0.7 : 1 }}>
          {saving ? '保存中...' : '🍽️ レシピを保存する'}
        </button>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '80px 20px 40px', backgroundColor: '#f4f7f9', minHeight: '100vh' },
  container: { maxWidth: '600px', margin: '0 auto' },
  heading: { fontSize: '22px', fontWeight: '900', color: '#2c3e50', marginBottom: '20px', textAlign: 'center' },
  section: { marginBottom: '20px' },
  label: { display: 'block', fontWeight: 'bold', fontSize: '13px', color: '#2c3e50', marginBottom: '8px' },
  required: { color: '#e74c3c' },
  charCount: { fontWeight: 'normal', color: '#95a5a6', fontSize: '12px' },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box',
    outline: 'none', marginBottom: '0', fontFamily: 'inherit',
    backgroundColor: '#fff',
  },
  imageArea: {
    width: '100%', height: '200px', borderRadius: '16px',
    border: '2px dashed #ddd', marginBottom: '20px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', overflow: 'hidden', backgroundColor: '#fafafa',
  },
  imagePreview: { width: '100%', height: '100%', objectFit: 'cover' },
  imagePlaceholder: { textAlign: 'center' },
  toggleBtn: {
    flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
    fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s',
  },
  suggestionBox: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
    background: '#fff', border: '1px solid #ddd', borderRadius: '10px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto',
  },
  suggestionItem: {
    padding: '10px 14px', cursor: 'pointer', fontSize: '14px',
    borderBottom: '1px solid #f0f0f0',
  },
  presetBtn: {
    padding: '4px 10px', borderRadius: '20px', border: '1px solid #ddd',
    background: '#fff', fontSize: '12px', cursor: 'pointer', color: '#555',
  },
  addBtn: {
    width: '100%', padding: '12px', borderRadius: '10px',
    border: '2px dashed #ddd', background: 'transparent',
    color: '#95a5a6', fontSize: '14px', cursor: 'pointer', marginTop: '4px',
  },
  deleteBtn: {
    padding: '8px 10px', borderRadius: '8px', border: 'none',
    background: '#fdeaea', color: '#e74c3c', cursor: 'pointer', fontSize: '12px', flexShrink: 0,
  },
  stepNumber: {
    width: '28px', height: '28px', borderRadius: '50%',
    background: '#e74c3c', color: '#fff', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontWeight: 'bold', fontSize: '13px', flexShrink: 0, marginTop: '8px',
  },
  submitBtn: {
    width: '100%', padding: '18px', borderRadius: '40px',
    background: '#e74c3c', color: '#fff', border: 'none',
    fontWeight: 'bold', fontSize: '16px', cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(231,76,60,0.3)',
  },
};
